"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { fadeUp } from "@/lib/motion";
import { PillBadge } from "@/components/ui/PillBadge";
import { FAQAccordion } from "@/components/ui/FAQAccordion";
import { GradientDivider } from "@/components/ui/GradientDivider";
import { BookCallSection } from "@/components/home/BookCallSection";
import { AccentColor } from "@/lib/colors";
import { CheckIcon } from "@/components/ui/Icons/CheckIcon";
import { CrossIcon } from "@/components/ui/Icons/CrossIcon";
import { PartialIcon } from "@/components/ui/Icons/PartialIcon";

type Mark = true | false | null | [true | null, string];

function renderMark(m: Mark): React.ReactNode {
  if (m === true) return <CheckIcon />;
  if (m === false) return <CrossIcon />;
  if (m === null) return <PartialIcon />;
  const [check, label] = m;
  return (
    <span className="inline-flex items-center gap-1">
      {check ? <CheckIcon /> : <PartialIcon />}
      <span className="text-text-muted">{label}</span>
    </span>
  );
}

/* ── TOC sections ── */
const TOC = [
  { id: "quick-answer", label: "Quick Answer" },
  { id: "what-to-look-for", label: "What to Look For" },
  { id: "comparison", label: "All 6 at a Glance" },
  { id: "scaffcalc", label: "1. Scaffcalc" },
  { id: "layplan", label: "2. LayPLAN Classic" },
  { id: "avontus", label: "3. Avontus" },
  { id: "scaffmax", label: "4. Scaffmax" },
  { id: "nti", label: "5. NTI Scaffplanner" },
  { id: "scaffplan", label: "6. ScaffPlan" },
  { id: "decision-points", label: "Key Decision Points" },
  { id: "faq", label: "FAQ" },
  { id: "bottom-line", label: "The Bottom Line" }
];

function useActiveSection() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px" }
    );
    for (const { id } of TOC) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return active;
}

/* ── Tool card ── */
function ToolSection({
  id,
  num,
  name,
  platform,
  image,
  imageAlt,
  children,
  strengths,
  limitations,
  systems,
  bestFor,
  pricing
}: {
  id: string;
  num: string;
  name: string;
  platform: string;
  image?: string;
  imageAlt?: string;
  children: React.ReactNode;
  strengths: string[];
  limitations: string[];
  systems: string;
  bestFor: string;
  pricing: string;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mt-10 lg:mt-16 mb-2">
        {num}. {name}
      </h2>
      <p className="text-sm text-text-muted font-body mb-6">
        Platform: {platform}
      </p>
      {image && (
        <img
          src={image}
          alt={imageAlt || name}
          className="w-full rounded-xl border border-border mb-8"
          loading="lazy"
        />
      )}
      <div className="prose-body mb-8">{children}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-bg-surface rounded-xl p-6">
          <h4 className="font-display text-sm font-bold text-success-text uppercase tracking-widest mb-3">
            Strengths
          </h4>
          <ul className="space-y-2">
            {strengths.map((s, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-text-body"
              >
                <CheckIcon />
                {s}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-bg-surface rounded-xl p-6">
          <h4 className="font-display text-sm font-bold text-warning-text uppercase tracking-widest mb-3">
            Limitations
          </h4>
          <ul className="space-y-2">
            {limitations.map((l, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-text-body"
              >
                <PartialIcon />
                {l}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="text-sm text-text-body space-y-1 mb-4">
        <p>
          <span className="text-text-primary font-bold">
            Compatible systems:
          </span>{" "}
          {systems}
        </p>
        <p>
          <span className="text-text-primary font-bold">Best for:</span>{" "}
          {bestFor}
        </p>
        <p>
          <span className="text-text-primary font-bold">Pricing:</span>{" "}
          {pricing}
        </p>
      </div>
    </section>
  );
}

/* ── Main article ── */
export function BestScaffoldingSoftwareArticle() {
  const activeSection = useActiveSection();
  const t = useTranslations("BestSoftwareArticle");

  return (
    <>
      {/* Hero */}
      <section className="pt-6 lg:pt-32 pb-8 lg:pb-12 px-6">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <PillBadge label={t("heroPill")} variant={AccentColor.Bubblegum} />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-text-primary mt-4">
              {t("heroTitle")}
            </h1>
            <p className="mt-4 text-text-body max-w-2xl mx-auto">
              {t("heroDescription")}
            </p>
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-text-muted">
              <span>{t("heroAuthor")}</span>
              <span>{t("heroReadTime")}</span>
              <span>{t("heroUpdated")}</span>
            </div>
          </motion.div>
          <video
            className="w-full max-w-3xl mx-auto rounded-2xl border border-border mt-8"
            aria-label="Scaffcalc demo: from blueprint to 3D scaffold model"
            autoPlay
            loop
            muted
            playsInline
          >
            <source
              src="/videos/home-clarity-from-blueprint.webm"
              type="video/webm"
            />
          </video>
          <p className="text-xs text-text-muted mt-2 italic">
            {t("heroCaption")}
          </p>
        </div>
      </section>

      <GradientDivider variant={AccentColor.Bubblegum} />

      {/* Body + sidebar */}
      <section className="py-16 px-6">
        <div className="mx-auto max-w-7xl flex gap-8">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-56 shrink-0 sticky top-24 self-start">
            <p className="font-display text-xs font-bold uppercase tracking-widest text-text-body mb-4">
              Table of Contents
            </p>
            <nav className="space-y-1">
              {TOC.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(item.id)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={`block text-sm py-1 transition-colors duration-150 ${
                    activeSection === item.id
                      ? "text-bubblegum-text font-bold"
                      : "text-text-muted hover:text-text-primary-text"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Article content */}
          <article className="flex-1 min-w-0 max-w-3xl">
            {/* Quick Answer */}
            <section id="quick-answer" className="scroll-mt-24 mb-8 md:mb-12">
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <h2 className="font-display text-xl font-bold text-text-primary mb-3">
                  Quick Answer
                </h2>
                <p className="text-sm text-text-body leading-relaxed">
                  <strong>Scaffcalc</strong> is the strongest all-in-one
                  platform for European contractors who design, calculate, and
                  quote in one browser-based tool.{" "}
                  <strong>LayPLAN Classic</strong> works well for Layher-only
                  operations. <strong>Avontus</strong> is the go-to for fleet
                  and inventory management. <strong>Scaffmax</strong> speeds up
                  layout in SketchUp. <strong>NTI Scaffplanner</strong> fits
                  Revit-based BIM teams. <strong>ScaffPlan</strong> integrates
                  into Tekla Structures with inbuilt structural checks.
                </p>
                <p className="text-sm text-text-body leading-relaxed mt-3">
                  No scaffolding software does everything equally well. This
                  guide covers six tools being used by scaffolding contractors
                  across Europe and North America. What each one does well,
                  where it falls short, and who it's the right fit for.
                </p>
              </div>
            </section>

            {/* What to Look For */}
            <section
              id="what-to-look-for"
              className="scroll-mt-24 mb-8 md:mb-12"
            >
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mb-6">
                What to Look for in Scaffolding Software
              </h2>
              <div className="space-y-4 text-sm text-text-body leading-relaxed">
                <p>
                  <strong className="text-text-primary">
                    3D design and modelling.
                  </strong>{" "}
                  Can you build the scaffold in 3D, and does that model
                  automatically drive your material list? If you're still
                  drawing in 2D and counting components by hand, you're leaving
                  time and margin on the table.
                </p>
                <p>
                  <strong className="text-text-primary">
                    Structural load calculations.
                  </strong>{" "}
                  Wind loads, anchor forces, standard pressures, and ground
                  forces are not optional on regulated job sites. The question
                  is whether your software handles them in-house or pushes you
                  to a separate consultant every time.
                </p>
                <p>
                  <strong className="text-text-primary">
                    Quotation and BOM accuracy.
                  </strong>{" "}
                  A material take-off tied directly to the 3D model eliminates
                  manual errors that eat into margin. Look for auto-generated
                  BOMs with live pricing, not exports that need a separate
                  spreadsheet.
                </p>
                <p>
                  <strong className="text-text-primary">
                    Platform and access.
                  </strong>{" "}
                  Desktop software locks you to a machine. Web-based tools run
                  anywhere and make it easy to share designs with clients,
                  subcontractors, and site managers.
                </p>
              </div>
            </section>

            {/* Comparison Table */}
            <section id="comparison" className="scroll-mt-24 mb-8 md:mb-12">
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mb-6">
                All 6 Tools at a Glance
              </h2>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-xs text-text-body">
                  <thead>
                    <tr className="bg-bg-surface">
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        Feature
                      </th>
                      <th className="text-left p-3 font-display font-bold text-lavender-text">
                        Scaffcalc
                      </th>
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        LayPLAN
                      </th>
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        Avontus
                      </th>
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        Scaffmax
                      </th>
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        NTI
                      </th>
                      <th className="text-left p-3 font-display font-bold text-text-primary">
                        ScaffPlan
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(
                      [
                        [
                          "Platform",
                          "Web (browser)",
                          "Desktop",
                          "Desktop",
                          "SketchUp plugin",
                          "Revit plugin",
                          "Tekla + SketchUp"
                        ],
                        ["3D Design", true, true, true, true, true, true],
                        [
                          "Structural Calcs",
                          [true, "Full"],
                          false,
                          false,
                          false,
                          false,
                          [null, "Checks"]
                        ],
                        ["Auto BOM", true, true, true, true, true, true],
                        [
                          "Quoting / PDF",
                          [true, "Branded"],
                          null,
                          null,
                          false,
                          false,
                          null
                        ],
                        ["IFC / BIM", true, false, false, null, true, true],
                        ["No Install", true, false, false, false, false, false],
                        [
                          "Shared Links",
                          true,
                          false,
                          false,
                          false,
                          false,
                          false
                        ]
                      ] as (string | Mark)[][]
                    ).map((row, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "" : "bg-bg-surface/50"}
                      >
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className={`p-3 ${j === 0 ? "font-bold text-text-primary" : ""}`}
                          >
                            {typeof cell === "string"
                              ? cell
                              : renderMark(cell as Mark)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Tool sections */}
            <ToolSection
              id="scaffcalc"
              num="1"
              name="Scaffcalc"
              platform="Web-based (browser)"
              image="/images/articles/juf-house-with-trs-hires.webp"
              imageAlt="Scaffcalc 3D canvas showing scaffold with TRS system"
              strengths={[
                "Only tool with full structural load calculations in the design canvas",
                "Runs in any browser, shareable via link, no installation",
                "Auto BOM tied to 3D model with live pricing",
                "Branded PDF quotations in minutes",
                "18 scaffold systems across 12 suppliers",
                "IFC import, point cloud import, DXF/RFEM export",
                "50% faster quoting vs pen-and-paper (verified by XERVON)"
              ]}
              limitations={[
                "Structural calculations currently Layher Allround only",
                "Europe-focused (US gets design + quoting, not calcs)",
                "Requires internet connection",
                "Engineering Services is a separate paid service"
              ]}
              systems="AT-PAC Metric/Imperial, Haki Universal, Layher Allround/SpeedyScaf, Scafom-Rux Ringscaff, Plus8, PERI UP Flex/Easy, Super 9, MJ Gerüst Combi/Optima, Plettac Perfect Contur/SL70/SL100, Solideq Ramscaff/Aluscaff, Alustar, StepUp Octo"
              bestFor="European contractors who need quoting, planning, and structural verification in one tool."
              pricing="Contact Scaffcalc for pricing."
            >
              <p className="text-sm text-text-body leading-relaxed">
                Scaffcalc is a web-based scaffolding platform built in Sweden,
                used by 140+ companies across 13 countries. It centres around a
                3D drag-and-drop canvas that runs in any browser. You design the
                scaffold, the model generates a material list automatically, and
                branded PDF quotations come out in minutes.
              </p>
              <p className="text-sm text-text-body leading-relaxed mt-3">
                What sets Scaffcalc apart is structural calculations built
                directly into the design workflow. From the 3D model, you set
                wind speed, terrain type, ground type, and load class. Scaffcalc
                applies all load combinations automatically, producing
                displacements, component forces, anchor forces, and ground
                forces.
              </p>
              <video
                className="w-full rounded-xl border border-border my-6"
                aria-label="Scaffcalc bridging engineering: structural analysis crossfade"
                autoPlay
                loop
                muted
                playsInline
              >
                <source
                  src="/videos/bridging-engineering-crossfade.webm"
                  type="video/webm"
                />
              </video>
            </ToolSection>

            <ToolSection
              id="layplan"
              num="2"
              name="LayPLAN Classic"
              platform="Desktop (Windows)"
              image="/images/articles/layplan-classic-interface.webp"
              imageAlt="LayPLAN Classic desktop interface"
              strengths={[
                "Deep Layher Allround component library",
                "Long track record in European markets",
                "Familiar to contractors in the Layher ecosystem",
                "Precise 3D visualisation and component reporting"
              ]}
              limitations={[
                "Layher Allround only",
                "No integrated structural load calculations",
                "Desktop-only, no browser access",
                "No branded quotation output",
                "No IFC/BIM import"
              ]}
              systems="Layher Allround only"
              bestFor="Contractors who exclusively use Layher Allround and need detailed material planning."
              pricing="Licence-based. Contact Layher or authorised dealers."
            >
              <p className="text-sm text-text-body leading-relaxed">
                LayPLAN Classic is a long-established desktop tool built for
                Layher Allround scaffolding. It lets you design scaffolds in 2D
                and 3D, generate component lists, and produce reports. Its
                advantage is deep Layher component knowledge.
              </p>
              <p className="text-sm text-text-body leading-relaxed mt-3">
                What it does not do is structural calculations or customer-ready
                quotations. Because it's tied exclusively to Layher, it's not
                viable for multi-system contractors.
              </p>
            </ToolSection>

            <ToolSection
              id="avontus"
              num="3"
              name="Avontus Scaffold Designer"
              platform="Desktop (Windows/Mac)"
              image="/images/articles/avontus-designer-interface.webp"
              imageAlt="Avontus Designer interface"
              strengths={[
                "Strong inventory and asset management for large fleets",
                "Established in US and UK enterprise markets",
                "Inventory-linked material lists",
                "Supports multiple scaffold systems",
                "Built for multi-depot logistics"
              ]}
              limitations={[
                "No integrated structural calculations",
                "Desktop-only",
                "Less suited to quoting-first workflows",
                "Lower adoption in continental Europe",
                "Enterprise pricing can be high for mid-size contractors"
              ]}
              systems="Multiple systems, primarily US/UK product lines"
              bestFor="Large US or UK companies where fleet and inventory management is the primary challenge."
              pricing="Enterprise custom pricing. Contact Avontus directly."
            >
              <p className="text-sm text-text-body leading-relaxed">
                Avontus is a US-based scaffolding software company. Their core
                strength is large companies with complex logistics: multiple
                yards, hundreds of components, fleet tracking. The design side
                handles 3D layout and generates material lists linked to actual
                inventory.
              </p>
            </ToolSection>

            <ToolSection
              id="scaffmax"
              num="4"
              name="Scaffmax"
              platform="SketchUp Pro plugin"
              image="/images/articles/scaffmax-sketchup-interface.webp"
              imageAlt="Scaffmax SketchUp plugin interface"
              strengths={[
                "Fast auto-build from 3D geometry",
                "Fits into existing SketchUp workflows",
                "Good visual output for client presentations",
                "Broad system support"
              ]}
              limitations={[
                "Requires SketchUp Pro (additional cost)",
                "No structural calculations",
                "Per-system pricing adds up",
                "No web access or shareable links",
                "Not designed for quoting workflows"
              ]}
              systems="Multiple (each system is a separate purchase)"
              bestFor="Contractors in the SketchUp ecosystem who want faster scaffold layout and visual output."
              pricing="Per-system licence. Requires SketchUp Pro subscription."
            >
              <p className="text-sm text-text-body leading-relaxed">
                Scaffmax is a scaffold design plugin for SketchUp Pro. It
                auto-builds scaffolding from 3D surfaces: you draw the
                structure, apply the surface, and Scaffmax places scaffold
                components around it automatically. Fast for initial layout and
                visualisation.
              </p>
            </ToolSection>

            <ToolSection
              id="nti"
              num="5"
              name="NTI Scaffplanner"
              platform="Autodesk Revit plugin"
              image="/images/articles/nti-scaffplanner-revit-interface.webp"
              imageAlt="NTI Scaffplanner Revit plugin interface"
              strengths={[
                "Native Revit integration for BIM coordination",
                "Strong fit for large-scale multi-discipline projects",
                "Accurate component extraction from BIM model",
                "Good adoption in Nordic markets"
              ]}
              limitations={[
                "Requires Autodesk Revit licence (significant cost)",
                "Steep learning curve, built for BIM-trained users",
                "No structural calculations or wind load analysis",
                "No quotation output",
                "Not practical for day-to-day contractor quoting"
              ]}
              systems="HAKI, Layher, Alusafe, Alustar, Solidec"
              bestFor="BIM-heavy projects where scaffolding must be coordinated inside Autodesk Revit."
              pricing="Requires Revit licence. Contact NTI for pricing."
            >
              <p className="text-sm text-text-body leading-relaxed">
                NTI Scaffplanner is a Revit plugin for workflows where the
                scaffold design lives inside the BIM model. The integration
                means scaffold designs sit natively inside the architectural and
                structural model, valuable for large construction projects with
                multi-discipline coordination.
              </p>
            </ToolSection>

            <ToolSection
              id="scaffplan"
              num="6"
              name="ScaffPlan"
              platform="Tekla Structures + SketchUp plugin"
              image="/images/articles/scaffplan-tekla-interface.webp"
              imageAlt="ScaffPlan Tekla Structures interface"
              strengths={[
                "Inbuilt structural checks with colour-coded pass/fail",
                "Tekla Structures integration with full BIM coordination",
                "SketchUp plugin for concept design",
                "Global product, multiple markets",
                "Active development, strong BIM community"
              ]}
              limitations={[
                "Requires Tekla Structures or SketchUp (significant cost)",
                "Checks cover member behaviour, not full wind/anchor/ground analysis",
                "No web access, tied to host software",
                "Not designed for quoting output",
                "Steeper onboarding without Tekla experience"
              ]}
              systems="Multiple"
              bestFor="Projects where Tekla Structures is the BIM authoring platform."
              pricing="Licence-based. Contact ScaffPlan directly."
            >
              <p className="text-sm text-text-body leading-relaxed">
                ScaffPlan operates as a plugin for Trimble's Tekla Structures,
                with an additional SketchUp plugin. Its distinguishing feature
                is inbuilt structural calculations displaying colour-coded
                pass/fail indicators in real time. The scope covers structural
                member behaviour, though not the complete load chain that a full
                structural calculation package covers.
              </p>
            </ToolSection>

            {/* Decision Points */}
            <section
              id="decision-points"
              className="scroll-mt-24 mt-10 lg:mt-16 mb-8 md:mb-12"
            >
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mb-6">
                Key Decision Points
              </h2>
              <div className="space-y-4 text-sm text-text-body leading-relaxed">
                <p>
                  <strong className="text-text-primary">
                    If you need structural load calculations:
                  </strong>{" "}
                  Scaffcalc is the only tool covering the full load chain.
                  ScaffPlan includes member checks but not wind/anchor/ground
                  force analysis.
                </p>
                <p>
                  <strong className="text-text-primary">
                    If you need quotation speed:
                  </strong>{" "}
                  Scaffcalc is the only tool built around branded commercial
                  quotations from the 3D model.
                </p>
                <p>
                  <strong className="text-text-primary">
                    If you work across multiple systems:
                  </strong>{" "}
                  Scaffcalc, Avontus, and Scaffmax support multiple systems.
                  LayPLAN is Layher-only.
                </p>
                <p>
                  <strong className="text-text-primary">
                    If you manage a large fleet:
                  </strong>{" "}
                  Avontus Quantify is the strongest inventory solution on this
                  list.
                </p>
                <p>
                  <strong className="text-text-primary">
                    If you work in BIM environments:
                  </strong>{" "}
                  NTI Scaffplanner for Revit, ScaffPlan for Tekla. Scaffcalc
                  supports IFC import/export without requiring a full BIM
                  platform.
                </p>
                <p>
                  <strong className="text-text-primary">
                    If platform independence matters:
                  </strong>{" "}
                  Only Scaffcalc runs in the browser with no installation and
                  shareable design links.
                </p>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-24 mb-8 md:mb-12">
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mb-6">
                Frequently Asked Questions
              </h2>
              <FAQAccordion
                defaultOpen={0}
                items={[
                  {
                    question:
                      "What is the best scaffolding software for small to mid-size contractors?",
                    answer:
                      "For most European contractors who need to quote quickly, plan accurately, and verify structural loads, Scaffcalc is the most complete single tool available. It runs in the browser, requires no CAD background, and is the only option that integrates structural calculations. It supports 18 scaffold systems and is used by 140+ companies across 13 countries."
                  },
                  {
                    question:
                      "Does scaffolding software calculate wind loads and structural forces?",
                    answer:
                      "Most tools do not include full structural load calculations. ScaffPlan includes inbuilt structural member checks with colour-coded pass/fail. Scaffcalc provides the most complete integrated analysis: displacements, component forces, anchor forces, and ground forces, calculated from the same 3D model used for quoting."
                  },
                  {
                    question:
                      "Can scaffolding software import IFC or BIM files?",
                    answer:
                      "Scaffcalc supports IFC import. NTI Scaffplanner operates natively inside Revit. ScaffPlan runs inside Tekla with native IFC support. LayPLAN Classic and Avontus do not natively support IFC import."
                  },
                  {
                    question:
                      "What scaffolding software works in a web browser?",
                    answer:
                      "Scaffcalc is the only scaffolding design and calculation platform that runs entirely in a web browser with no installation required."
                  }
                ]}
              />
            </section>

            {/* Bottom Line */}
            <section id="bottom-line" className="scroll-mt-24 mb-8 md:mb-12">
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary mb-6">
                The Bottom Line
              </h2>
              <div className="text-sm text-text-body leading-relaxed space-y-3">
                <p>
                  The best scaffolding software is the one that fits your actual
                  workflow.
                </p>
                <p>
                  If you run a large US or UK fleet, Avontus solves a real
                  logistics problem. If you work exclusively with Layher
                  Allround, LayPLAN Classic has decades of pedigree. If your
                  work lives inside a BIM model with a Revit-trained team, NTI
                  Scaffplanner integrates cleanly.
                </p>
                <p>
                  For most European scaffolding contractors running multi-system
                  operations, competing on quote speed, and increasingly
                  expected to provide structural verification alongside
                  proposals, Scaffcalc brings the most into one place: design,
                  calculation, and quotation, without requiring a CAD background
                  or an external engineer on every job.
                </p>
              </div>
            </section>

            {/* Author */}
            <div className="border-t border-border pt-8 mt-12">
              <p className="text-xs text-text-muted uppercase tracking-widest mb-2">
                About the Author
              </p>
              <p className="text-sm text-text-body">
                <strong className="text-text-primary">Kevin Walian</strong> is a
                content writer at Scaffcalc with a focus on scaffolding industry
                workflows, software tools, and structural compliance
                documentation.
              </p>
              <p className="text-xs text-text-muted mt-2 italic">
                Updated April 2026
              </p>
            </div>
          </article>
        </div>
      </section>

      <BookCallSection />
    </>
  );
}
